import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { Session, ClassContent, PracticeResultMap, VocabResult, PracticeResult, VocabRecordAttempts, VocabRecordType } from "../types/session";
import { UserRole } from "../types/user";
import { sessionService } from '../services';
import { useUserStore } from './user';
import { useClassStore } from './class';
import { SignalRRoom } from "../types/signalr";
import { PracticeQuizState } from "../types/class";
import { toast } from "vue3-toastify";
import { useLangStore } from "./lang";
import { TEXT } from "../configs/text";
import localforage from "localforage";


export const useSessionStore = defineStore('session', () => {
    const user = useUserStore()
    const classStore = useClassStore()
    const lang = useLangStore()

    const currentSession = ref<Session>(Object.values(Session)[0])
    const teacherSession = ref<Session | null>(null)
    const classContent = ref<ClassContent | null>(null)
    // splitting practice url out of class content due to user_id change when updating quiz/lecture data
    const practiceUrl = ref<string | null>(null)
    const isFullscreen = ref<boolean>(user.userRole === UserRole.student && teacherSession ? true : false)

    // vocab state
    const allowSpeakingStudentIds = ref<number[]>([])
    const currentVocabId = ref<number>(0)
    const teacherVocabId = ref<number>(0)
    const vocabRecordingType = ref<number>(VocabRecordType.word)
    const vocabRecordAttempts = ref<VocabRecordAttempts>({})
    const recordAttemptCount = computed<number>((): number => {
        return vocabRecordAttempts.value[classStore.roomName]?.[user.username || '']?.[currentVocabId.value]?.[vocabRecordingType.value]?.length || 0
    })

    // practice state
    const practiceQuizState = ref<PracticeQuizState | null>(null)
    const practiceResult = ref<PracticeResultMap>({})
    const currentPracticeResult = computed<PracticeResult | null>(() => {
        if (!classStore.roomName || !user.username) return null;
        return practiceResult.value[classStore.roomName]?.[user.username] || null
    })

    async function getClassContent() {
        let classContentData: ClassContent | null = null;
        if (user.userRole === UserRole.student) {
            classContentData = await sessionService.getStudentClassContent()
        } else {
            const classId = classStore.selectedClassDetails?.id
            if (classId) {
                classContentData = await sessionService.getTeacherClassContent(classId)
            }
        }

        const practiceUrlData = classContentData?.[Session.practice]
        // no practice url = not setup data in CMS
        if (!practiceUrlData) {
            classContent.value = null
            practiceUrl.value = null
            return
        }

        classContent.value = classContentData
        practiceUrl.value = practiceUrlData
    }

    async function submitAudioVocab(sentence: string, audioFile: Blob, type: number): Promise<VocabResult | null> {
        if (!classStore.roomName) return null;
        if (!user.username) return null;
        if (!currentVocabId.value) return null;

        const vocabResult = await sessionService.submitAudioVocab(sentence, audioFile, type)
        if (!vocabResult) return null;

        // save audio blob to index storage for later playback
        const audioKey = crypto.randomUUID()
        await localforage.setItem(audioKey, audioFile)
        vocabResult.key = audioKey

        vocabRecordAttempts.value[classStore.roomName] ??= {}
        vocabRecordAttempts.value[classStore.roomName][user.username] ??= {}
        vocabRecordAttempts.value[classStore.roomName][user.username][currentVocabId.value] ??= {}
        vocabRecordAttempts.value[classStore.roomName][user.username][currentVocabId.value][vocabRecordingType.value] ??= []
        vocabRecordAttempts.value[classStore.roomName][user.username][currentVocabId.value][vocabRecordingType.value].push(vocabResult)
        return vocabResult
    }

    async function setPracticeResult(result: PracticeResult) {
        if (!classStore.roomName || !user.username) return;

        if (!practiceResult.value[classStore.roomName]) {
            practiceResult.value[classStore.roomName] = {}
        }

        practiceResult.value[classStore.roomName][user.username] = result
    }

    function syncSession(room: SignalRRoom) {
        if (user.userRole === UserRole.student) {
            if (room.currentScreen) {
                currentSession.value = room.currentScreen
            }
            if (parseInt(room.currentQuestion || '0') && room.listStudentAnswer.some(roomUser => parseInt(roomUser.id) === user.userInfo?.id)) {
                currentVocabId.value = parseInt(room.currentQuestion || '0')
            }
        }
        teacherSession.value = room.currentScreen
        teacherVocabId.value = parseInt(room.currentQuestion || '0')
        practiceQuizState.value = room.practiceStart
        allowSpeakingStudentIds.value = room.listStudentAnswer.map(user => {
            return parseInt(user.id)
        })
    }

    function syncClassContent(content: ClassContent | undefined | null) {
        classContent.value = content || null
    }

    watch(allowSpeakingStudentIds, (newList, oldList) => {
        if (user.userRole !== UserRole.student) return;
        if (oldList.includes(user.userInfo?.id || 0)) return;
        if (!newList.includes(user.userInfo?.id || 0)) return;
        if (currentSession.value !== Session.vocab) return;

        toast.info(lang.i18n.global.t(TEXT.you_can_now_record_your_speaking))
    })

    watch(teacherSession, () => {
        if (user.userRole !== UserRole.student) return;
        if (!teacherSession.value) {
            isFullscreen.value = false
            return
        }
        isFullscreen.value = true
    })

    return {
        currentSession,
        classContent,
        practiceUrl,
        getClassContent,
        teacherSession,
        isFullscreen,
        allowSpeakingStudentIds,
        currentVocabId,
        teacherVocabId,
        vocabRecordAttempts,
        recordAttemptCount,
        submitAudioVocab,
        practiceQuizState,
        practiceResult,
        currentPracticeResult,
        setPracticeResult,
        syncSession,
        syncClassContent,
        vocabRecordingType
    }
}, {
    persist: {
        pick: ['currentSession', 'teacherSession', 'practiceResult', 'currentVocabId', 'vocabRecordAttempts']
    }
})